import * as XLSX from 'xlsx';

const HEADER_ALIASES = {
  putawayId: ['putawayid', 'putaway id', 'putaway'],
  productSku: ['product sku', 'productsku', 'sku', 'product'],
  totalQuantity: ['total quantity', 'total qty', 'totalquantity'],
  requiredBins: ['required bins', 'required bin count', 'requiredbins'],
  slotNo: ['slot #', 'slot', 'slot no', 'slotno'],
  slotQuantity: ['slot quantity', 'slot qty', 'slotquantity', 'calculated qty'],
  condition: ['condition', 'bin type', 'bintype', 'type'],
  binCode: ['bin code', 'bincode', 'bin'],
};

function normalizeHeader(str) {
  return String(str ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
}

function mapHeader(cell) {
  const n = normalizeHeader(cell);
  if (!n) return null;
  for (const [field, aliases] of Object.entries(HEADER_ALIASES)) {
    if (aliases.includes(n)) return field;
  }
  return null;
}

function normalizeCondition(raw) {
  const c = String(raw ?? '').trim().toUpperCase();
  if (!c) return '';
  if (c === 'GOOD') return 'GOOD';
  if (c === 'HOLD' || c === 'DAMAGED') return 'HOLD';
  return c;
}

/**
 * Parse downloaded bulk allocation Excel — validates system-generated columns only.
 * Used after backend template download (Condition / Bin Code may still be empty).
 */
export function parsePutawayBulkExcelStructure(buffer, selectedPutawayIds = []) {
  const { rows, errors: parseErrors } = parsePutawayBulkExcelMatrix(buffer, {
    validateUserInput: false,
  });

  const errors = [...(parseErrors || [])];
  const selectedIdSet = new Set((selectedPutawayIds || []).map((id) => String(id).trim()).filter(Boolean));
  const uploadedPutawayIds = new Set(rows.map((r) => r.putawayId).filter(Boolean));

  if (!rows.length) {
    errors.push({
      error:
        'Downloaded Excel has no allocation rows. Ensure selected putaways have received quantity and product qty-per-bin configured.',
    });
  }

  for (const id of selectedIdSet) {
    if (!uploadedPutawayIds.has(id)) {
      errors.push({
        putawayId: id,
        error: `Putaway ${id} is missing from the downloaded allocation worksheet`,
      });
    }
  }

  for (const row of rows) {
    if (!row.putawayId) {
      errors.push({ row: row.excelRow, error: 'Missing Putaway ID in a system-generated row' });
    }
    if (row.slotQuantity == null || row.slotQuantity <= 0) {
      errors.push({
        row: row.excelRow,
        putawayId: row.putawayId,
        error: 'Missing or invalid Slot Quantity in a system-generated row',
      });
    }
  }

  return { rows, errors, putawayCount: uploadedPutawayIds.size, slotCount: rows.length };
}

/**
 * Parse bulk putaway bin allocation Excel for client-side preview.
 * Quantities are read from Slot Quantity (system-generated) — never treated as user input.
 */
export function parsePutawayBulkExcelPreview(buffer) {
  return parsePutawayBulkExcelMatrix(buffer, { validateUserInput: true });
}

function parsePutawayBulkExcelMatrix(buffer, { validateUserInput = true } = {}) {
  const errors = [];
  const rows = [];

  if (!buffer) {
    return { rows, errors: ['Invalid or empty file'] };
  }

  let workbook;
  try {
    workbook = XLSX.read(buffer, { type: 'array', cellDates: false });
  } catch (e) {
    return { rows, errors: [`Unable to read Excel file: ${e.message}`] };
  }

  const sheetName =
    workbook.SheetNames.find((n) => normalizeHeader(n) === 'bulk bin allocation') ||
    workbook.SheetNames.find((n) => normalizeHeader(n) === 'data') ||
    workbook.SheetNames.find((n) => normalizeHeader(n) === 'upload') ||
    workbook.SheetNames[0];

  if (!sheetName) {
    return { rows, errors: ['Excel file has no worksheets'] };
  }

  const matrix = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], {
    header: 1,
    defval: '',
    raw: true,
    blankrows: false,
  });

  if (!matrix.length) {
    return { rows, errors: ['Excel worksheet is empty'] };
  }

  const headerRowIndex = matrix.findIndex((row) => (row || []).some((cell) => mapHeader(cell)));
  if (headerRowIndex < 0) {
    return {
      rows,
      errors: ['Required columns not found. Use the downloaded template (Putaway ID, Bin Code, Condition).'],
    };
  }

  const colIndex = {};
  (matrix[headerRowIndex] || []).forEach((cell, idx) => {
    const field = mapHeader(cell);
    if (field && colIndex[field] === undefined) colIndex[field] = idx;
  });

  const required = ['putawayId', 'binCode', 'condition'];
  const missing = required.filter((f) => colIndex[f] === undefined);
  if (missing.length) {
    return {
      rows,
      errors: [`Missing required column(s): ${missing.join(', ')}`],
    };
  }

  for (let i = headerRowIndex + 1; i < matrix.length; i++) {
    const line = matrix[i] || [];
    const putawayId = String(line[colIndex.putawayId] ?? '').trim();
    const binCode = String(line[colIndex.binCode] ?? '').trim();
    const condition = normalizeCondition(line[colIndex.condition]);
    const slotQtyRaw = colIndex.slotQuantity !== undefined ? line[colIndex.slotQuantity] : '';
    const slotQty = Number(slotQtyRaw);

    if (!putawayId && !binCode && !condition) continue;

    const excelRow = i + 1;
    const row = {
      excelRow,
      putawayId,
      productSku: colIndex.productSku !== undefined ? String(line[colIndex.productSku] ?? '').trim() : '',
      totalQuantity: colIndex.totalQuantity !== undefined ? line[colIndex.totalQuantity] : '',
      requiredBins: colIndex.requiredBins !== undefined ? line[colIndex.requiredBins] : '',
      slotNo: colIndex.slotNo !== undefined ? line[colIndex.slotNo] : '',
      slotQuantity: Number.isFinite(slotQty) ? slotQty : null,
      condition,
      binCode,
      clientStatus: 'pending',
    };

    if (!putawayId) {
      if (validateUserInput) {
        errors.push({ row: excelRow, field: 'Putaway ID', error: 'Putaway ID is required' });
      }
      row.clientStatus = 'invalid';
    } else if (validateUserInput && !binCode) {
      errors.push({ row: excelRow, putawayId, field: 'Bin Code', error: 'Bin Code is required' });
      row.clientStatus = 'invalid';
    } else if (validateUserInput && !condition) {
      errors.push({ row: excelRow, putawayId, field: 'Condition', error: 'Condition is required (GOOD or HOLD)' });
      row.clientStatus = 'invalid';
    } else if (validateUserInput && condition !== 'GOOD' && condition !== 'HOLD') {
      errors.push({
        row: excelRow,
        putawayId,
        field: 'Condition',
        error: `Invalid condition. Use GOOD or HOLD.`,
      });
      row.clientStatus = 'invalid';
    } else if (validateUserInput) {
      row.clientStatus = 'ready';
    } else {
      row.clientStatus = binCode && condition ? 'ready' : 'pending';
    }

    rows.push(row);
  }

  return { rows, errors };
}

export function groupPreviewRowsByPutaway(previewRows) {
  const map = new Map();
  for (const row of previewRows || []) {
    const key = row.putawayId || '—';
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(row);
  }
  return map;
}
